import * as openpgp from "openpgp";
import ebiOfficeService from "api/ebiOfficeService";
import {
  IEbiKeyRequestClaim,
  IEbiKeyShareItem,
  IEbiKeyShareResult,
} from "dto/Ebi/IEbiOffice";
import { encryptionKeyIdHex } from "./buildCompanyKeyPayload";
import { getCompanyPrivateKeys, hasCompanyPrivateKeys } from "./companyContentKeys";
import { getContentKeys } from "./contentKeys";
import { IDecryptedKeys } from "./decryptKeys";
import { invalidateDecryptedKeys } from "./decryptPackage";
import { base64ToBytes, readPublicKey, verifyEbiKeyBinding } from "./ebiKeyBinding";
import { getActiveCompanyKey, getCompanyPublicKeyBase64 } from "./encryptForCompany";
import { getKeys } from "./getKeys";
import { readAsBase64 } from "./readAsBase64";

// EBI, etap 4 — przeszyfrowanie kluczy obiektów przesyłek EBI dla petentów (docs/ebi-plan.md).
// Serwer nie zna klucza prywatnego firmy, więc robi to przeglądarka pracownika: przejmuje zadanie z kolejki,
// odszyfrowuje klucz obiektu (kluczem firmy, a dla przesyłek w teczce — kluczem teczki), sprawdza, że to właściwy
// klucz, i zawija go DOKŁADNIE dla [klucz firmy, bieżący klucz petenta]. BE przyjmuje wyłącznie taką pulę.
//
// Przeglądarka działa tu bez udziału człowieka, więc nie może być „wyrocznią deszyfrującą” dla podstawionych danych:
// odszyfrowuje WYŁĄCZNIE kluczami firmy (nigdy kluczem pośrednim pracownika ani kluczem gościa), wymaga klucza
// publicznego obiektu, odrzuca wynik, który jest którymkolwiek z kluczy trzymanych w tej karcie, a przede wszystkim
// bierze tylko klucz z ważnym podpisem kluczem firmy pod powiązaniem z doręczeniem EBI (decyzja D1, utils/ebiKeyBinding).

/** Ile zadań najwyżej w jednym przebiegu (kolejne przy następnym sygnale albo odpytaniu). */
const MAX_REQUESTS_PER_RUN = 20;

/** Po odmowie BE (brak uprawnień, EBI poza firmą) karta wraca do kolejki po tym czasie albo po zmianie kluczy firmy. */
const UNAVAILABLE_FOR_MS = 15 * 60 * 1000;

export type EbiKeyShareRunStatus =
  | "done"
  | "idle"
  | "busy"
  | "unavailable"
  | "keyMismatch"
  | "failed";

export interface IEbiKeyShareRun {
  status: EbiKeyShareRunStatus;
  shared: number;
}

const decryptWith = async (base64: string, privateKeys: openpgp.key.Key[]): Promise<Uint8Array> => {
  const message = await openpgp.message.read(base64ToBytes(base64));
  const { data } = await openpgp.decrypt({ message, privateKeys, format: "binary" });

  return data as Uint8Array;
};

/** Odciski kluczy, których przeszyfrowanie nigdy nie może wydać: firma (każda generacja w pamięci), gość, klucz pośredni. */
const forbiddenFingerprints = async (
  companyKeys: openpgp.key.Key[],
  myKeys: IDecryptedKeys | null,
): Promise<Set<string>> => {
  const result = new Set<string>(
    [...companyKeys, ...getContentKeys("guest")].map((k) => k.getFingerprint()),
  );

  try {
    const armored = myKeys?.intermidatePrivateKey?.data;

    if (armored) {
      const intermediate = await openpgp.key.readArmored(atob(armored));
      intermediate.keys.forEach((k) => result.add(k.getFingerprint()));
    }
  } catch {
    // brak klucza pośredniego — nie ma czego dopisać
  }

  return result;
};

/**
 * Klucz prywatny obiektu (binarny packetlist) z pierwszego źródła, które da się odszyfrować kluczem firmy
 * (bezpośrednio albo przez klucz teczki zawinięty kluczem firmy).
 */
const decryptObjectKey = async (
  item: IEbiKeyShareItem,
  companyKeys: openpgp.key.Key[],
): Promise<Uint8Array> => {
  for (const source of item.Sources) {
    try {
      if (!source.ParentKeyId) {
        return await decryptWith(source.PrivateKey, companyKeys);
      }

      if (!source.ParentPrivateKey) continue;

      const parent = (await openpgp.key.read(await decryptWith(source.ParentPrivateKey, companyKeys))).keys[0];
      if (!parent) continue;

      return await decryptWith(source.PrivateKey, [parent]);
    } catch {
      // to źródło nie jest dla klucza firmy — następne
    }
  }

  throw new Error("Nie udało się odszyfrować klucza przesyłki kluczem firmy.");
};

/**
 * Przeszyfrowuje klucze przejętego zadania. Fail-closed: jeśli którykolwiek klucz się nie uda, nie oddajemy nic —
 * zadanie wraca do kolejki (BE i tak przyjmuje wynik tylko w całości od przejmującego).
 */
export const shareEbiKeyRequest = async (
  claim: IEbiKeyRequestClaim,
  myKeys: IDecryptedKeys | null,
): Promise<IEbiKeyShareResult[]> => {
  const companyKeys = getCompanyPrivateKeys();
  if (!companyKeys.length) throw new Error("Brak klucza firmy w tej karcie.");

  const companyKey = await readPublicKey(await getCompanyPublicKeyBase64());
  const petentKey = await readPublicKey(claim.PetentPublicKey);

  // Klucz firmy do zawinięcia musi być jednym z kluczy, które ta karta naprawdę trzyma (nie tylko deklaracją serwera),
  // a jego aktywna generacja — tą, której wymaga BE.
  if (!companyKeys.some((k) => k.getFingerprint() === companyKey.getFingerprint())) {
    throw new Error("Klucz publiczny firmy nie pasuje do kluczy firmy w tej karcie — odśwież aplikację.");
  }

  const companyKeyId = await encryptionKeyIdHex(companyKey).catch(() => null);
  if (!companyKeyId || companyKeyId.toLowerCase() !== claim.CompanyKeyIdHex.toLowerCase()) {
    throw new Error("Klucz firmy w przeglądarce nie jest aktywną generacją — odśwież aplikację.");
  }

  const forbidden = await forbiddenFingerprints(companyKeys, myKeys);
  forbidden.add(petentKey.getFingerprint());

  const { WorkspaceId: workspaceId } = await getActiveCompanyKey();
  const results: IEbiKeyShareResult[] = [];

  for (const item of claim.Items) {
    if (!item.ObjectPublicKey) throw new Error("Pozycja bez klucza publicznego przesyłki.");

    const expected = (await readPublicKey(item.ObjectPublicKey)).getFingerprint();
    if (forbidden.has(expected)) throw new Error("Pozycja wskazuje klucz, którego nie wolno udostępniać.");

    // D1: zanim cokolwiek odszyfrujemy — nadawca podpisał kluczem firmy, że to klucz przesyłki EBI do tego podmiotu
    await verifyEbiKeyBinding(item, expected, workspaceId, companyKeys);

    const objectKey = await decryptObjectKey(item, companyKeys);
    const actual = (await openpgp.key.read(objectKey)).keys[0]?.getFingerprint();

    if (!actual || actual !== expected || forbidden.has(actual)) {
      throw new Error("Odszyfrowany klucz nie pasuje do klucza przesyłki.");
    }

    const { message } = await openpgp.encrypt({
      message: openpgp.message.fromBinary(objectKey),
      publicKeys: [companyKey, petentKey],
      armor: false,
    });

    results.push({
      ObjectKeyId: item.ObjectKeyId,
      PrivateKey: await readAsBase64(new Uint8Array(message.packets.write())),
    });
  }

  return results;
};

let running = false;

/** Do kiedy ta karta nie obsługuje kolejki (odmowa BE); 0 — obsługuje. */
let unavailableUntil = 0;

/** Ręczne „Udostępnij teraz” i zmiana kluczy firmy znoszą wcześniejszą odmowę. */
export const resetEbiKeyShareAvailability = (): void => {
  unavailableUntil = 0;
};

const httpStatus = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } })?.response?.status;

/**
 * Obsługa kolejki przeszyfrowań EBI firmy: przejmuje zadania, aż kolejka będzie pusta (albo limit przebiegu).
 * Jedna na kartę naraz; między kartami i pracownikami pilnuje BE (atomowe przejęcie z dzierżawą).
 */
export const processEbiKeyRequests = async (userId: string): Promise<IEbiKeyShareRun> => {
  if (running) return { status: "busy", shared: 0 };
  if (Date.now() < unavailableUntil || !hasCompanyPrivateKeys()) return { status: "unavailable", shared: 0 };

  running = true;

  let shared = 0;

  try {
    let summary;

    try {
      summary = (await ebiOfficeService.getKeyRequestsSummary()).data;
    } catch (error) {
      const status = httpStatus(error);
      // 400 = EBI poza firmą, 403 = brak uprawnień — ta karta na razie nie obsługuje kolejki
      if (status === 400 || status === 403) unavailableUntil = Date.now() + UNAVAILABLE_FOR_MS;
      return { status: "unavailable", shared: 0 };
    }

    if (!summary.CanProcess) {
      unavailableUntil = Date.now() + UNAVAILABLE_FOR_MS;
      return { status: "unavailable", shared: 0 };
    }

    if (summary.Pending === 0) return { status: "idle", shared: 0 };

    // Inna generacja klucza firmy w tej karcie — przejęcie tylko spaliłoby próbę zadania (BE odrzuci wynik).
    const localKeyId = await encryptionKeyIdHex(await readPublicKey(await getCompanyPublicKeyBase64())).catch(() => null);

    if (summary.CompanyKeyIdHex && localKeyId?.toLowerCase() !== summary.CompanyKeyIdHex.toLowerCase()) {
      return { status: "keyMismatch", shared: 0 };
    }

    // null przy koncie bez klucza pośredniego — potrzebny tylko do listy kluczy, których nie wolno wydać
    const myKeys = await getKeys(userId).catch(() => null);

    for (let i = 0; i < MAX_REQUESTS_PER_RUN; i++) {
      // wylogowanie albo zmiana firmy w trakcie przebiegu — nie przejmujemy dalej
      if (!hasCompanyPrivateKeys()) break;

      let claim: IEbiKeyRequestClaim | null;

      try {
        claim = await ebiOfficeService.claimKeyRequest();
      } catch (error) {
        const status = httpStatus(error);
        if (status === 400 || status === 403) unavailableUntil = Date.now() + UNAVAILABLE_FOR_MS;
        return { status: shared > 0 ? "done" : "unavailable", shared };
      }

      if (!claim) break;

      try {
        const keys = await shareEbiKeyRequest(claim, myKeys);
        await ebiOfficeService.completeKeyRequest(claim.RequestId, keys);

        // Cache odszyfrowanych kluczy trzyma wersje sprzed przeszyfrowania.
        invalidateDecryptedKeys(keys.map((k) => k.ObjectKeyId));
        shared += keys.length;
      } catch (error) {
        console.error("EBI: nie udało się udostępnić przesyłek petentowi", claim.RequestId, error);

        // Zadanie wraca na koniec kolejki (po kilku próbach — stan „błąd” w ustawieniach EBI). W tym przebiegu
        // już go nie bierzemy: ponowne przejęcie od razu tylko spaliłoby kolejną próbę.
        await ebiOfficeService
          .failKeyRequest(
            claim.RequestId,
            error instanceof Error ? error.message : "Błąd przeszyfrowania w przeglądarce.",
          )
          .catch(() => undefined);

        return { status: "failed", shared };
      }
    }
  } finally {
    running = false;
  }

  return { status: shared > 0 ? "done" : "idle", shared };
};
